import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import PhoneInput from 'react-phone-input-2';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles, FormHelperText } from '@material-ui/core';
import 'react-phone-input-2/lib/material.css';
import { useTranslation } from 'react-i18next';
import allActions from '../../../store/actions';
import { CUSTOM_MASKS } from './phoneNumberCountries';
import { defaultCountryPhoneCodeSelector } from '../../../store/selectors/globalConfigSelectors';
import { COUNTRY_CODE } from '../../../constants/countryList';

const useInputStyles = makeStyles((theme) => ({
  wrapper: {
    marginBottom: '16px',
    maxWidth: '514px',
    width: '100%',
  },
  input: {
    width: '100%!important',
    paddingTop: '10.5px!important',
    paddingBottom: '10.5px!important',
  },
  button: {
    '&:before': {
      color: theme.palette.grey[600],
    },
  },
  helperText: {
    position: 'absolute',
  },
}));

const InputPhoneNumber = ({ hint, required, label, form, field, defaultCountryCode = COUNTRY_CODE.ITALY }) => {
  const { setFieldValue, errors, touched } = form;
  const { name, onChange } = field;

  const defaultCountry = defaultCountryCode ? defaultCountryCode.toLocaleLowerCase() : useSelector(defaultCountryPhoneCodeSelector);
  const dispatch = useDispatch();

  const classes = useInputStyles();
  const { t } = useTranslation();

  const [digitsAmount, setDigitsAmount] = useState(10);
  const [currentCountry, setCurrentCountry] = useState(null);
  const [dirty, setDirty] = useState(false);

  const containerRef = useRef(null);

  const labelToDisplay = `${t(label)}${required ? ' *' : ''}`;

  if (containerRef.current) {
    containerRef.current.dropdownContainerRef.setAttribute('data-before', labelToDisplay);
  }

  const calculateDigistsAmount = (format) => {
    let length = 0;
    format.split('').forEach((char) => {
      if (char === '.') length++;
    });

    return length;
  };

  const onValueChange = (phoneNumber, selectedCountry) => {
    const { dialCode } = selectedCountry;
    const dotPosition = dialCode?.length ?? 0;
    const formattedTelNumber = `+${dialCode}.${phoneNumber.substr(dotPosition)}`;

    setFieldValue(name, formattedTelNumber);
    if (!currentCountry) {
      setCurrentCountry(selectedCountry);
    }

    if (selectedCountry.name !== currentCountry?.name) {
      setCurrentCountry(selectedCountry);
      setDigitsAmount(calculateDigistsAmount(selectedCountry.format));
    }

    if (onChange !== null) {
      onChange(phoneNumber);
    }
  };

  useEffect(() => {
    if (currentCountry) {
      dispatch(
        allActions.formConfigActions.setTelephoneNumberFormat(
          { ...currentCountry, digitsAmount },
          name,
        ),
      );
    }
  }, [digitsAmount, dispatch, name]);

  const handleBlur = () => {
    setDirty(true);
  };

  const shouldDisplayError = () => (touched[name] || dirty) && !!errors[name];

  return (
    <div className={classes.wrapper}>
      <PhoneInput
        enableTerritories
        masks={CUSTOM_MASKS}
        isValid={() => {
          if (touched[name] && errors[name]) return labelToDisplay;
          if (!dirty || !errors[name]) return true;
          return labelToDisplay;
        }}
        ref={containerRef}
        placeholder=""
        hand
        name={name}
        onChange={onValueChange}
        buttonClass={classes.button}
        inputClass={classes.input}
        specialLabel={labelToDisplay}
        onBlur={handleBlur}
        country={defaultCountry}
        value={field.value}
      />
      <FormHelperText
        className={classes.helperText}
        variant="filled"
        error={shouldDisplayError()}
        required
        margin="dense"
      >
        {shouldDisplayError() ? errors[name] : hint}
      </FormHelperText>
    </div>
  );
};

InputPhoneNumber.propTypes = {
  required: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
};

InputPhoneNumber.defaultProps = {
  hint: null,
};

export default InputPhoneNumber;
